import { memo, useEffect } from 'react';
import { Tag } from '@nimbus-ds/components';
import { AiFeature } from '@tiendanube/common/src/enums';
import { Stack } from '@tiendanube/components';
import { DownloadIcon, ListIcon, PlusCircleIcon } from '@tiendanube/icons';
import { FEATURE_AI_TIER } from 'App/features';
import {
  AI_PRODUCTS_FROM_IMAGES,
  AI_SINGLE_PRODUCT_FROM_IMAGES,
} from 'App/featuresFlags';
import HelpLink from 'App/HelpLink';
import { useHasTags, useNavegate } from 'App/hooks';
import { useBackgroundJobs } from 'App/settings/jobs/hooks';
import { PopoverMenu } from 'commons/components';
import { AiIcon } from 'commons/components/GenerateWithAiLink';
import {
  ActionProp,
  ActionsDesktop,
  ApplicationsDesktop,
  HeaderContent,
  HeaderTop,
  IonPageStratus,
} from 'commons/components/IonPageStratus';
import { LendingAwareness } from 'commons/components/LendingAwareness/LendingAwareness';
import Responsive, { useResponsive } from 'commons/components/Responsive';
import { CAN_LINK_ABOUT_PRODUCTS } from 'commons/constants';
import {
  useModal,
  useStorage,
  useTranslationCommon,
  useTranslationLanguage,
} from 'commons/hooks';
import { useAiUsage } from 'domains/Ai/hooks';
import { trackingAiGenerateCtaClick } from 'domains/Ai/tracking';
import { useGetAllLanguages, useHasPermission } from 'domains/Auth/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import {
  AlertMetafields,
  ErrorMetafieldAlert,
} from 'domains/Metafields/components';
import { useAppsLinks } from 'domains/PartnersApps/hooks';
import useLocations from 'domains/Shipping/Locations/hooks/useLocations';
import {
  AICameraIcon,
  AlertMassAction,
  BulkActions,
  BulkActionsProvider,
  CreateFromImagesModal,
  ProductListEmpty,
  ProductListSearch,
  ProductResultsDesktop,
  ProductResultsMobile,
  useBulkActions,
} from './components';
import { AlertEditPrices } from './components/BulkActions/AlertEditPrices';
import { ProductsFiltersType } from '../../productsServices';
import {
  trackingProductListAddProductAiClick,
  trackingProductListAddProductClick,
  trackingProductListExportImportClick,
  trackingProductListOrganizeClick,
  trackingProductListPageView,
} from '../../tracking';
import useProductsList from '../hooks/useProductsList';

function ProductListPageWithTrasnlations(): JSX.Element {
  return (
    <BulkActionsProvider>
      <ProductListPageMemo />
    </BulkActionsProvider>
  );
}

const ProductListPageMemo = memo(ProductListPage);

function ProductListPage(): JSX.Element {
  const t = useTranslationCatalog();
  const tCommon = useTranslationCommon();
  const language = useTranslationLanguage();
  const languages = useGetAllLanguages();
  const { goTo } = useNavegate();
  const [hasAIProductsFromImages, hasAISingleProductFromImages] = useHasTags([
    AI_PRODUCTS_FROM_IMAGES,
    AI_SINGLE_PRODUCT_FROM_IMAGES,
  ]);
  const [showProductUpdates, setShowProductUpdates, loadedProductUpdates] =
    useStorage('product-update-create-product-with-ai', true);

  const { isLoadingAiUses, requiredTier, aiUsesUpsellTitle } = useAiUsage();

  const {
    toggleEditMode,
    setSelectedRowsId,
    setSelectAll,
    selectAll,
    editMode,
    selectedRowsId,
    showAlertMetafields,
  } = useBulkActions();
  const { getAppsLinks } = useAppsLinks();
  const { jobStatus } = useBackgroundJobs('editPricesProducts');
  const { isMobile, isDesktop } = useResponsive();
  const canImportExportProductCsv = useHasPermission(
    'import_export_products_csv',
  );
  const canCreateProductsFromImages =
    hasAIProductsFromImages &&
    !isLoadingAiUses &&
    isDesktop &&
    languages.length === 1;

  const canCreateProductWithAI =
    hasAISingleProductFromImages && !isLoadingAiUses;

  const {
    isLoading,
    isSuccess,
    statusSuccess,
    productsIds,
    productsCount,
    filters,
    hasFilters,
    getProductsList,
    removeMetafieldsFilters,
    refreshProductsList,
  } = useProductsList();

  const { fetchLocations } = useLocations();

  const isEmpty = isSuccess && productsIds.length === 0 && !hasFilters;

  const [
    isCreateFromImagesModalOpen,
    openCreateFromImagesModal,
    closeCreateFromImagesModal,
  ] = useModal(false);

  useEffect(() => {
    if (jobStatus !== 'ready') getProductsList();
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filters.page === 1 && jobStatus === 'ready') refreshProductsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatus]);

  useEffect(() => {
    if (statusSuccess === 'errorMetafield') {
      removeMetafieldsFilters();
    }
  }, [filters, removeMetafieldsFilters, statusSuccess]);

  useEffect(() => {
    trackingProductListPageView();
  }, []);

  const editModeAction = {
    onClick: toggleEditMode,
    children: editMode
      ? `${t('common:editMode.cancel')}`
      : `${t('common:editMode.edit')}`,
  };

  const handleOnClickAdd = () => {
    trackingProductListAddProductClick();
    goTo('/products/new');
  };

  const handleOnClickAddFromImages = () => {
    trackingAiGenerateCtaClick({ feature: AiFeature.AI_PRODUCTS_FROM_IMAGES });
    goTo('/products/new-from-images');
  };

  const handleOnClickImportExport = () => {
    trackingProductListExportImportClick();
    goTo('/products/import/');
  };

  const handleChangeFilters = (newFilters: ProductsFiltersType) => {
    getProductsList(newFilters);
    if (selectAll) {
      setSelectAll(false);
      setSelectedRowsId([]);
    }
  };

  const handleOpenAddFromImagesUpsell = useUpsellFlow({
    title: aiUsesUpsellTitle,
    featureKey: FEATURE_AI_TIER,
    trackingSource: AiFeature.AI_PRODUCTS_FROM_IMAGES,
    callback: handleOnClickAddFromImages,
    minValue: requiredTier,
  });

  const handleOpenAddWithAIAUpsell = useUpsellFlow({
    title: aiUsesUpsellTitle,
    featureKey: FEATURE_AI_TIER,
    trackingSource: AiFeature.AI_PRODUCTS_FROM_IMAGES,
    callback: openCreateFromImagesModal,
    minValue: requiredTier,
  });

  const orderProductsAction: ActionProp = {
    children: `${t('products.orderProducts')}`,
    onClick: () => {
      trackingProductListOrganizeClick();
      goTo('/products/organize');
    },
    icon: ListIcon,
  };
  const exportImportAction: ActionProp = {
    children: `${t('products.importExport')}`,
    onClick: handleOnClickImportExport,
    icon: DownloadIcon,
  };
  const addProductAction: ActionProp = {
    children:
      canCreateProductWithAI && isMobile
        ? t('products.addProductManually')
        : t('products.addProduct'),
    icon: PlusCircleIcon,
    appearance: 'primary',
    onClick: handleOnClickAdd,
  };

  const addProductFromImagesAction: ActionProp = {
    children: t('products.addProductFromImages'),
    icon: AICameraIcon,
    onClick: handleOpenAddFromImagesUpsell,
  };

  const addProductGroup: ActionProp = {
    children: t('products.addProduct'),
    isGroup: true,
  };

  const addProductWithAIAction: ActionProp = {
    children: t('products.addProductWithAI'),
    icon: AiIcon,
    onClick: () => {
      trackingProductListAddProductAiClick();
      setShowProductUpdates(false);
      handleOpenAddWithAIAUpsell();
    },
    isNew: true,
    productUpdates: {
      width: '272px',
      title: t('products.fromImages.productUpdates.title'),
      description: t('products.fromImages.productUpdates.description'),
      dismissLink: t('products.fromImages.productUpdates.dismissLink'),
      visible: loadedProductUpdates && showProductUpdates,
      onClick: () => {
        setShowProductUpdates(false);
      },
    },
  };

  const actionConfig = [
    { action: orderProductsAction, condition: true },
    { action: exportImportAction, condition: canImportExportProductCsv },
    {
      action: addProductFromImagesAction,
      condition: canCreateProductsFromImages,
    },
    {
      action: addProductGroup,
      condition: canCreateProductWithAI && isMobile,
    },
    {
      action: addProductWithAIAction,
      condition: canCreateProductWithAI,
    },
    { action: addProductAction, condition: true },
  ];

  const actions: ActionProp[] = actionConfig
    .filter((item) => item.condition)
    .map((item) => item.action);

  const tagNew = <Tag appearance="primary">{tCommon('menu.new')}</Tag>;

  const actionsMobile = actions.map((action) => ({
    label: action.children ?? '',
    startIcon: action.icon,
    onClick: action.onClick,
    children: action.isNew ? tagNew : '',
    isGroup: action.isGroup,
  }));

  const renderMenuMobile = (
    <PopoverMenu actions={actionsMobile} buttonColor="transparent" />
  );

  const applicationsDesktop: ActionProp[] = getAppsLinks(
    'products',
    'default',
  ).map((app) => ({
    children: app.text,
    onClick: () => window.open(app.url, '_blank'),
  }));

  const helpLink = (
    <HelpLink
      title={t('helpLink.aboutProducts')}
      linkURL={CAN_LINK_ABOUT_PRODUCTS[language]}
      currentViewTracking={t('helpLink.aboutProductsTrackingName')}
      previousValue="product_list"
    />
  );

  return (
    <IonPageStratus
      width="medium"
      headerTop={
        selectedRowsId.length === 0 && (
          <HeaderTop
            mainAction={isEmpty ? undefined : editModeAction}
            actions={isEmpty ? undefined : renderMenuMobile}
          />
        )
      }
      headerContent={
        isEmpty ? undefined : (
          <HeaderContent
            title={t('products.title')}
            actions={
              <ActionsDesktop actions={actions} renderActionsAsButtons />
            }
            applications={
              <ApplicationsDesktop applications={applicationsDesktop} />
            }
          />
        )
      }
      headerFixed
    >
      {isEmpty ? (
        <ProductListEmpty />
      ) : (
        <>
          <AlertMetafields
            show={showAlertMetafields}
            ownerResource="product_variant"
          />
          <AlertEditPrices />
          <AlertMassAction
            jobId="assignCategories"
            keyPrefix="products.massAction.assignCategories"
          />
          <ErrorMetafieldAlert status={statusSuccess} />
          <BulkActions />
          <LendingAwareness app="products" />
          <Stack column align="stretch" spacing={isMobile ? 'tight' : 'base'}>
            <ProductListSearch
              filters={filters}
              productsCount={productsCount}
              isLoading={isLoading}
              onChange={handleChangeFilters}
            />
            <Responsive
              mobile={
                <ProductResultsMobile
                  isEditMode={editMode}
                  onToggleEditMode={toggleEditMode}
                />
              }
              desktop={<ProductResultsDesktop />}
            />
          </Stack>
          {helpLink}
        </>
      )}
      <CreateFromImagesModal
        open={isCreateFromImagesModalOpen}
        onDismiss={closeCreateFromImagesModal}
      />
    </IonPageStratus>
  );
}

export default memo(ProductListPageWithTrasnlations);
